import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        width: '100%',
    }
}));

const Sedes = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        sedes: [],
        sede: {
            idsede: 0,
            code: '',
            name: '',
            time_zone: ''
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Sede';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/sedes');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, sedes: response?.data ?? [] }));
        } else {
            console.log('[Sedes] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, sede: { ...prev.sede, [target.name]: target.value } }));

    const validateForm = (type) => {
        const { idsede, code, name, time_zone } = state.sede;

        switch (type) {
            case 'update':
                const data = state.sedes.filter(value => value.idsede === idsede);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.sede);
                    if (before !== after) {
                        if (code.trim() && name.trim() && time_zone.trim()) {
                            return true;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some sede', 'error');
                    return false;
                }

            case 'store':

                if (code.trim() && name.trim() && time_zone.trim()) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (idsede === 0) {
                    showToast('Select some sede', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this sede?');

                return Boolean(idsede && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, sede: { ...prev.sede, idsede: 0, code: '', name: '', time_zone: '' } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/sede/store', state.sede);
            if (response.status === 200) {
                setState(prev => ({ ...prev, sedes: response?.data ?? prev.sedes }));
                showToast(response.message);
            } else {
                console.log('[Sedes] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idsede } = state.sede;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/sede/${idsede}/update`, state.sede);
            if (response.status === 200) {
                setState(prev => ({ ...prev, sedes: response?.data ?? prev.sedes }));
                showToast(response.message);
            } else {
                console.log('[Sedes] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idsede } = state.sede;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/sede/${idsede}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, sedes: response?.data ?? prev.sedes }));
                showToast(response.message);
            } else {
                console.log('[Sedes] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/sedes/${row.idsede}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'name', headerName: 'Name', flex: 2 },
        { field: 'time_zone', headerName: 'Time zone', flex: 1.5 },
        { field: 'actions', headerName: 'Actions', flex: 1, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <h1>{'Sedes'}</h1>
            <div>
                <Grid container>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>{'Code'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Code'
                                    name='code'
                                    value={state.sede.code}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.sede.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>{'Time zone'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Time zone'
                                    name='time_zone'
                                    value={state.sede.time_zone}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='sedes' id='idsede' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.sedes}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idsede}
                    loading={state.loading}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, sede: row }))}
                />
            </div>
        </div>
    );
}

export default Sedes;